/**
 * Hook for managing CURP details (name, birth info, etc.)
 */
import { useMemo, useState } from "react";
import { useSignup as useSignupContext } from "../../SignupContext";
import { validateName, validateLastName, validateBirthDay, validateBirthMonth, validateBirthYear, validateGender, validateStateOfBirth, } from "../../validation/personal";
export function useCurpDetails() {
    const { formValues, setFormValues } = useSignupContext();
    // Check if values were loaded from sessionStorage
    const hasLoadedValues = formValues.loadedFromStorage;
    // Initial states for touched and blurred
    const initialFirstNameTouched = hasLoadedValues && formValues.curpDetails.firstName.length > 0;
    const initialFirstNameBlurred = initialFirstNameTouched;
    const initialPaternalLastNameTouched = hasLoadedValues && formValues.curpDetails.paternalLastName.length > 0;
    const initialPaternalLastNameBlurred = initialPaternalLastNameTouched;
    const initialMaternalLastNameTouched = hasLoadedValues && formValues.curpDetails.maternalLastName.length > 0;
    const initialMaternalLastNameBlurred = initialMaternalLastNameTouched;
    const initialGenderTouched = hasLoadedValues && formValues.curpDetails.gender.length > 0;
    const initialGenderBlurred = initialGenderTouched;
    const initialDayOfBirthTouched = hasLoadedValues && formValues.curpDetails.dayOfBirth.length > 0;
    const initialDayOfBirthBlurred = initialDayOfBirthTouched;
    const initialMonthOfBirthTouched = hasLoadedValues && formValues.curpDetails.monthOfBirth.length > 0;
    const initialMonthOfBirthBlurred = initialMonthOfBirthTouched;
    const initialYearOfBirthTouched = hasLoadedValues && formValues.curpDetails.yearOfBirth.length > 0;
    const initialYearOfBirthBlurred = initialYearOfBirthTouched;
    const initialStateOfBirthTouched = hasLoadedValues && formValues.curpDetails.stateOfBirth.length > 0;
    const initialStateOfBirthBlurred = initialStateOfBirthTouched;
    // State for tracking touch and blur states for each field
    const [firstNameTouched, setFirstNameTouched] = useState(initialFirstNameTouched);
    const [firstNameBlurred, setFirstNameBlurred] = useState(initialFirstNameBlurred);
    const [paternalLastNameTouched, setPaternalLastNameTouched] = useState(initialPaternalLastNameTouched);
    const [paternalLastNameBlurred, setPaternalLastNameBlurred] = useState(initialPaternalLastNameBlurred);
    const [maternalLastNameTouched, setMaternalLastNameTouched] = useState(initialMaternalLastNameTouched);
    const [maternalLastNameBlurred, setMaternalLastNameBlurred] = useState(initialMaternalLastNameBlurred);
    const [genderTouched, setGenderTouched] = useState(initialGenderTouched);
    const [genderBlurred, setGenderBlurred] = useState(initialGenderBlurred);
    const [dayOfBirthTouched, setDayOfBirthTouched] = useState(initialDayOfBirthTouched);
    const [dayOfBirthBlurred, setDayOfBirthBlurred] = useState(initialDayOfBirthBlurred);
    const [monthOfBirthTouched, setMonthOfBirthTouched] = useState(initialMonthOfBirthTouched);
    const [monthOfBirthBlurred, setMonthOfBirthBlurred] = useState(initialMonthOfBirthBlurred);
    const [yearOfBirthTouched, setYearOfBirthTouched] = useState(initialYearOfBirthTouched);
    const [yearOfBirthBlurred, setYearOfBirthBlurred] = useState(initialYearOfBirthBlurred);
    const [stateOfBirthTouched, setStateOfBirthTouched] = useState(initialStateOfBirthTouched);
    const [stateOfBirthBlurred, setStateOfBirthBlurred] = useState(initialStateOfBirthBlurred);
    // Validate all fields
    const validation = useMemo(() => {
        const firstNameValidation = validateName(formValues.curpDetails.firstName);
        const paternalLastNameValidation = validateLastName(formValues.curpDetails.paternalLastName);
        const maternalLastNameValidation = validateLastName(formValues.curpDetails.maternalLastName);
        const genderValidation = validateGender(formValues.curpDetails.gender);
        const dayValidation = validateBirthDay(formValues.curpDetails.dayOfBirth);
        const monthValidation = validateBirthMonth(formValues.curpDetails.monthOfBirth);
        const yearValidation = validateBirthYear(formValues.curpDetails.yearOfBirth);
        const stateValidation = validateStateOfBirth(formValues.curpDetails.stateOfBirth);
        return {
            firstName: firstNameValidation,
            paternalLastName: paternalLastNameValidation,
            maternalLastName: maternalLastNameValidation,
            gender: genderValidation,
            dayOfBirth: dayValidation,
            monthOfBirth: monthValidation,
            yearOfBirth: yearValidation,
            stateOfBirth: stateValidation,
            isValid: firstNameValidation.isValid &&
                paternalLastNameValidation.isValid &&
                maternalLastNameValidation.isValid &&
                genderValidation.isValid &&
                dayValidation.isValid &&
                monthValidation.isValid &&
                yearValidation.isValid &&
                stateValidation.isValid,
        };
    }, [formValues.curpDetails, formValues.language]); // Include language for reactivity
    // Field setter functions
    const setFirstName = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            firstName: value,
        });
    };
    const setPaternalLastName = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            paternalLastName: value,
        });
    };
    const setMaternalLastName = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            maternalLastName: value,
        });
    };
    const setGender = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            gender: value,
        });
    };
    const setDayOfBirth = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            dayOfBirth: value,
        });
    };
    const setMonthOfBirth = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            monthOfBirth: value,
        });
    };
    const setYearOfBirth = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            yearOfBirth: value,
        });
    };
    const setDateOfBirth = (value) => {
        setDayOfBirthTouched(true);
        setMonthOfBirthTouched(true);
        setYearOfBirthTouched(true);
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            dayOfBirth: value.day,
            monthOfBirth: value.month,
            yearOfBirth: value.year,
        });
    };
    const setDateOfBirthTouched = (touched) => {
        setDayOfBirthTouched(touched);
        setMonthOfBirthTouched(touched);
        setYearOfBirthTouched(touched);
    };
    const setDateOfBirthBlurred = (blurred) => {
        setDayOfBirthBlurred(blurred);
        setMonthOfBirthBlurred(blurred);
        setYearOfBirthBlurred(blurred);
    };
    const setStateOfBirth = (value) => {
        setFormValues.setCurpDetails({
            ...formValues.curpDetails,
            stateOfBirth: value,
        });
    };
    return useMemo(() => ({
        firstName: {
            value: formValues.curpDetails.firstName,
            touched: firstNameTouched,
            blurred: firstNameBlurred,
            isValid: validation.firstName.isValid,
            errorMessage: validation.firstName.errorMessage,
            setValue: setFirstName,
            setTouched: setFirstNameTouched,
            setBlurred: setFirstNameBlurred,
        },
        paternalLastName: {
            value: formValues.curpDetails.paternalLastName,
            touched: paternalLastNameTouched,
            blurred: paternalLastNameBlurred,
            isValid: validation.paternalLastName.isValid,
            errorMessage: validation.paternalLastName.errorMessage,
            setValue: setPaternalLastName,
            setTouched: setPaternalLastNameTouched,
            setBlurred: setPaternalLastNameBlurred,
        },
        maternalLastName: {
            value: formValues.curpDetails.maternalLastName,
            touched: maternalLastNameTouched,
            blurred: maternalLastNameBlurred,
            isValid: validation.maternalLastName.isValid,
            errorMessage: validation.maternalLastName.errorMessage,
            setValue: setMaternalLastName,
            setTouched: setMaternalLastNameTouched,
            setBlurred: setMaternalLastNameBlurred,
        },
        gender: {
            value: formValues.curpDetails.gender,
            touched: genderTouched,
            blurred: genderBlurred,
            isValid: validation.gender.isValid,
            errorMessage: validation.gender.errorMessage,
            setValue: setGender,
            setTouched: setGenderTouched,
            setBlurred: setGenderBlurred,
        },
        dayOfBirth: {
            value: formValues.curpDetails.dayOfBirth,
            touched: dayOfBirthTouched,
            blurred: dayOfBirthBlurred,
            isValid: validation.dayOfBirth.isValid,
            errorMessage: validation.dayOfBirth.errorMessage,
            setValue: setDayOfBirth,
            setTouched: setDayOfBirthTouched,
            setBlurred: setDayOfBirthBlurred,
        },
        monthOfBirth: {
            value: formValues.curpDetails.monthOfBirth,
            touched: monthOfBirthTouched,
            blurred: monthOfBirthBlurred,
            isValid: validation.monthOfBirth.isValid,
            errorMessage: validation.monthOfBirth.errorMessage,
            setValue: setMonthOfBirth,
            setTouched: setMonthOfBirthTouched,
            setBlurred: setMonthOfBirthBlurred,
        },
        yearOfBirth: {
            value: formValues.curpDetails.yearOfBirth,
            touched: yearOfBirthTouched,
            blurred: yearOfBirthBlurred,
            isValid: validation.yearOfBirth.isValid,
            errorMessage: validation.yearOfBirth.errorMessage,
            setValue: setYearOfBirth,
            setTouched: setYearOfBirthTouched,
            setBlurred: setYearOfBirthBlurred,
        },
        dateOfBirth: {
            value: {
                day: formValues.curpDetails.dayOfBirth,
                month: formValues.curpDetails.monthOfBirth,
                year: formValues.curpDetails.yearOfBirth,
            },
            touched: dayOfBirthTouched || monthOfBirthTouched || yearOfBirthTouched,
            blurred: dayOfBirthBlurred || monthOfBirthBlurred || yearOfBirthBlurred,
            isValid: validation.dayOfBirth.isValid &&
                validation.monthOfBirth.isValid &&
                validation.yearOfBirth.isValid,
            errorMessage: !validation.dayOfBirth.isValid
                ? validation.dayOfBirth.errorMessage
                : !validation.monthOfBirth.isValid
                    ? validation.monthOfBirth.errorMessage
                    : !validation.yearOfBirth.isValid
                        ? validation.yearOfBirth.errorMessage
                        : null,
            setValue: setDateOfBirth,
            setTouched: setDateOfBirthTouched,
            setBlurred: setDateOfBirthBlurred,
        },
        stateOfBirth: {
            value: formValues.curpDetails.stateOfBirth,
            touched: stateOfBirthTouched,
            blurred: stateOfBirthBlurred,
            isValid: validation.stateOfBirth.isValid,
            errorMessage: validation.stateOfBirth.errorMessage,
            setValue: setStateOfBirth,
            setTouched: setStateOfBirthTouched,
            setBlurred: setStateOfBirthBlurred,
        },
        isValid: validation.isValid,
        setCurpDetails: setFormValues.setCurpDetails,
    }), [
        formValues.curpDetails,
        validation,
        firstNameTouched,
        firstNameBlurred,
        paternalLastNameTouched,
        paternalLastNameBlurred,
        maternalLastNameTouched,
        maternalLastNameBlurred,
        genderTouched,
        genderBlurred,
        dayOfBirthTouched,
        dayOfBirthBlurred,
        monthOfBirthTouched,
        monthOfBirthBlurred,
        yearOfBirthTouched,
        yearOfBirthBlurred,
        stateOfBirthTouched,
        stateOfBirthBlurred,
        stateOfBirthBlurred,
        setFormValues.setCurpDetails,
    ]);
}
